import { useQuery } from '@tanstack/react-query';
import Config from 'src/common/Config';

type HealthStatus = 'healthy' | 'unhealthy';
type OverallHealthStatus = 'healthy' | 'degraded' | 'unhealthy' | 'unknown';

type ComponentHealth = {
  status: HealthStatus;
  message?: string;
};

type ModelHealth = {
  model: string;
  provider: string;
  status: HealthStatus;
  message?: string;
};

type LlmHealthDetail = {
  status: OverallHealthStatus;
  healthyCount: number;
  totalCount: number;
  models: ModelHealth[];
};

type AdminHealthResponse = {
  status: OverallHealthStatus;
  db: ComponentHealth;
  llm: LlmHealthDetail;
};

export async function fetchAdminHealth(probe: boolean): Promise<AdminHealthResponse> {
  const url = `${Config.getApiUrl()}/admin/health${probe ? '?probe=true' : ''}`;
  const response = await fetch(url, { credentials: 'include' });

  // Health endpoint returns 200/502/503 based on health status - all are valid responses with data
  if (response.status === 200 || response.status === 502 || response.status === 503) {
    return response.json();
  }

  throw new Error(`Failed to fetch health: ${response.status}`);
}

export const useAdminHealth = () => {
  const { data, isLoading, isError, error, refetch, isRefetching } = useQuery<AdminHealthResponse>({
    queryKey: ['admin', 'health'],
    queryFn: () => fetchAdminHealth(false),
  });

  return {
    health: data,
    isLoading,
    isError,
    error,
    refetch,
    isRefetching,
  };
};
